// src/features/group_users/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  InputField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { Tables } from "@/gen/tableNames/tables";

//
// Zod schema for the "New Group User" form.
// Only "name" and "client" are required per the Ruby form.
//
const FormSchema = z.object({
  // Required
  name: z.string().min(1, "Name is required"),

  client: z.preprocess(
    (val) => preprocessTextAsyncAutocompleteField(val),
    z.string().min(1, "Client is required"),
  ),

  // TODO: Add Provider Configs field back
  // AutocompleteAsyncField for Provider Configs (multiple)
  // provider_config_names: z.preprocess((val) => {
  //   if (Array.isArray(val)) {
  //     return val
  //       .filter((v) => v !== null)
  //       .map((v: any) =>
  //         typeof v === "object" && v !== null && "name" in v ? v : v
  //       );
  //   }
  //   return [];
  // }, z.array(z.union([z.string(), z.object({ id: z.number(), name: z.string() })])).optional()),
  // provider_config_ids: z.preprocess((val) => {
  //   if (Array.isArray(val)) {
  //     return val
  //       .filter((v) => v !== null)
  //       .map((v: any) =>
  //         typeof v === "object" && v !== null && "id" in v ? v.id : v
  //       );
  //   }
  //   return [];
  // }, z.array(z.number()).optional()),

  // Optional text fields
  default_url: z.string().nullable().optional(),
  tableau_username: z.string().nullable().optional(),
  tableau_id: z.string().nullable().optional(),

  // JSONB field: accept JSON text, parse into an object
  policies: z.preprocess((val) => {
    if (Array.isArray(val)) {
      return JSON.stringify(val, null, 2);
    }
    if (typeof val === "object" && val !== null) {
      return JSON.stringify(val, null, 2);
    }
    if (typeof val === "string" && val.trim() !== "") {
      try {
        JSON.parse(val); // Validate JSON
        return val;
      } catch {
        return "{}";
      }
    }
    if (typeof val === "string" && val.trim() === "") {
      return "{}"; // Return empty object string instead of empty string
    }
    if (val === null || val === undefined) {
      return "{}";
    }
    return String(val);
  }, z.string().nullable().optional()),
});

export function GroupUsersEditCard({
  id,
  client,
  table_name,
}: {
  id: string;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "client") {
        return {
          text: value,
          id: value,
          name: value,
        };
      }
      if (
        key === "default_company_name" &&
        fetchedData.default_company_id &&
        value
      ) {
        return {
          text: value,
          id: fetchedData.default_company_id,
          name: value,
        };
      }
      // TODO: Add Provider Configs adjustOnReset logic back
      // if (key === "provider_config_names" && fetchedData.provider_config_ids) {
      //   return fetchedData.provider_config_ids
      //     .filter((id: number) => id !== null)
      //     .map((id: number) => ({
      //       text: `Provider Config ${id}`,
      //       id: id,
      //       title: `Provider Config ${id}`,
      //     }));
      // }
      if (key === "policies") {
        if (Array.isArray(value)) {
          return JSON.stringify(value, null, 2);
        }
        if (typeof value === "object" && value !== null) {
          return JSON.stringify(value, null, 2);
        }
        if (typeof value === "string") {
          return value;
        }
        return "{}";
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                GroupUsers {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Name & Client */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${Tables.GROUP_USERS}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="client"
                    label={t(`${Tables.GROUP_USERS}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    base_url={`${client}/${Tables.GROUP_USERS}`}
                    column_name="client"
                    showId={false}
                    options={{ keepDuplicates: false }}
                    control={form.control}
                  />
                </div>

                {/* Row 2: Default Company */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="default_company_name"
                    label={t(
                      `${Tables.GROUP_USERS}.columns.default_company_name`,
                      {
                        ns: "tables",
                        defaultValue: "Default Company",
                      },
                    )}
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="default_company_id"
                    control={form.control}
                  />
                  {/* TODO: Add Provider Configs field back */}
                  {/* <AutocompleteAsyncField
                    name="provider_config_names"
                    label={t(`${tableName}.columns.name`, { ns: "tables", defaultValue: "Provider Configs" })}
                    base_url={`${client}/provider_configs`}
                    column_name="name"
                    id_column="provider_config_ids"
                    control={form.control}
                  /> */}
                </div>

                {/* Row 3: Default URL & Tableau Username */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="default_url"
                    label={t(`${Tables.GROUP_USERS}.columns.default_url`, {
                      ns: "tables",
                      defaultValue: "Default URL",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="tableau_username"
                    label={t(`${Tables.GROUP_USERS}.columns.tableau_username`, {
                      ns: "tables",
                      defaultValue: "Tableau Username",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 4: Tableau ID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="tableau_id"
                    label={t(`${Tables.GROUP_USERS}.columns.tableau_id`, {
                      ns: "tables",
                      defaultValue: "Tableau ID",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 5: Policies JSON */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="policies"
                    label={t(`${Tables.GROUP_USERS}.columns.policies`, {
                      ns: "tables",
                      defaultValue: "Policies (JSON)",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.json")}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [
                  "default_company_id",
                  // "provider_config_ids", // TODO: Add back when Provider Configs field is restored
                ])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel without saving */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
